import { expect } from '@open-wc/testing';
import { TextSelection } from 'prosemirror-state';
import {
  defaultSchema,
  htmlToDoc,
  docToHtml,
  createEditorState,
  getCommand,
  isExcelHtml,
  isOfficeHtml,
  cleanOfficeHtml,
  armFormatPainter,
  applyFormatPainter,
  isFormatPainterArmed,
  disarmFormatPainter,
} from './index';

/** Editor state with the whole document selected. */
function stateWithAllSelected(html: string) {
  const doc = htmlToDoc(defaultSchema, html);
  const state = createEditorState({ doc });
  const selection = TextSelection.create(state.doc, 1, state.doc.content.size - 1);
  return state.apply(state.tr.setSelection(selection));
}

/** Runs a command against a state and returns the resulting state. */
function run(state: ReturnType<typeof stateWithAllSelected>, name: Parameters<typeof getCommand>[1], attrs?: Parameters<typeof getCommand>[2]) {
  let next = state;
  const command = getCommand(state, name, attrs);
  command?.(state, tr => {
    next = state.apply(tr);
  });
  return next;
}

describe('changeCase command', () => {
  it('uppercases the selection', () => {
    const state = run(stateWithAllSelected('<p>Hello world</p>'), 'changeCase', { textCase: 'upper' });
    expect(docToHtml(state.doc)).to.equal('<p>HELLO WORLD</p>');
  });

  it('lowercases the selection', () => {
    const state = run(stateWithAllSelected('<p>HELLO</p>'), 'changeCase', { textCase: 'lower' });
    expect(docToHtml(state.doc)).to.equal('<p>hello</p>');
  });

  it('title-cases the selection', () => {
    const state = run(stateWithAllSelected('<p>hello brave world</p>'), 'changeCase', { textCase: 'title' });
    expect(docToHtml(state.doc)).to.equal('<p>Hello Brave World</p>');
  });

  it('sentence-cases the selection', () => {
    const state = run(stateWithAllSelected('<p>hello. there is more. ok</p>'), 'changeCase', {
      textCase: 'sentence',
    });
    expect(docToHtml(state.doc)).to.equal('<p>Hello. There is more. Ok</p>');
  });

  it('preserves marks across the transform', () => {
    const state = run(stateWithAllSelected('<p><strong>bold</strong> plain</p>'), 'changeCase', {
      textCase: 'upper',
    });
    expect(docToHtml(state.doc)).to.equal('<p><strong>BOLD</strong> PLAIN</p>');
  });

  it('is a no-op with an empty selection', () => {
    const doc = htmlToDoc(defaultSchema, '<p>hello</p>');
    const state = createEditorState({ doc });
    expect(getCommand(state, 'changeCase', { textCase: 'upper' })!(state, () => {})).to.be.false;
  });
});

describe('merge field', () => {
  it('inserts a merge-field node', () => {
    const doc = htmlToDoc(defaultSchema, '<p></p>');
    let state = createEditorState({ doc });
    state = state.apply(
      state.tr.setSelection(TextSelection.create(state.doc, 1))
    );
    const state2 = run(state, 'insertMergeField', { id: 'first_name', label: 'First name' });
    const out = docToHtml(state2.doc);
    expect(out).to.include('data-merge-field="first_name"');
    expect(out).to.include('First name');
  });

  it('round-trips through HTML', () => {
    const html = '<p><span data-merge-field="email" data-merge-label="Email">Email</span></p>';
    expect(docToHtml(htmlToDoc(defaultSchema, html))).to.include('data-merge-field="email"');
  });
});

describe('math', () => {
  it('inserts a math node', () => {
    const doc = htmlToDoc(defaultSchema, '<p></p>');
    let state = createEditorState({ doc });
    state = state.apply(state.tr.setSelection(TextSelection.create(state.doc, 1)));
    const state2 = run(state, 'insertMath', { latex: 'a^2 + b^2', mathDisplay: 'block' });
    const out = docToHtml(state2.doc);
    expect(out).to.include('data-math="a^2 + b^2"');
    expect(out).to.include('data-math-display="block"');
  });

  it('round-trips through HTML', () => {
    const html = '<p><span data-math="x/y" data-math-display="inline">x/y</span></p>';
    expect(docToHtml(htmlToDoc(defaultSchema, html))).to.include('data-math="x/y"');
  });
});

describe('format painter', () => {
  it('copies marks from one selection and applies to the next', () => {
    // <p>BOLD plain</p> — arm over the bold word, then apply to "plain".
    const doc = htmlToDoc(defaultSchema, '<p><strong>bold</strong> plain</p>');
    let state = createEditorState({ doc });
    // Select the bold word (positions 1..5).
    state = state.apply(state.tr.setSelection(TextSelection.create(state.doc, 1, 5)));
    state = state.apply(armFormatPainter(state)!);
    expect(isFormatPainterArmed(state)).to.be.true;

    // Select "plain" (positions 6..11) and apply.
    state = state.apply(state.tr.setSelection(TextSelection.create(state.doc, 6, 11)));
    const applyTr = applyFormatPainter(state);
    expect(applyTr).to.not.be.null;
    state = state.apply(applyTr!);

    // "plain" (6..11) becomes bold; the space at 5 stays plain, so the two
    // bold runs render as separate <strong> tags.
    expect(docToHtml(state.doc)).to.equal('<p><strong>bold</strong> <strong>plain</strong></p>');
    expect(isFormatPainterArmed(state)).to.be.false;
  });

  it('disarms without applying', () => {
    const doc = htmlToDoc(defaultSchema, '<p><strong>bold</strong></p>');
    let state = createEditorState({ doc });
    state = state.apply(state.tr.setSelection(TextSelection.create(state.doc, 1, 5)));
    state = state.apply(armFormatPainter(state)!);
    state = state.apply(disarmFormatPainter(state));
    expect(isFormatPainterArmed(state)).to.be.false;
  });
});

describe('Excel paste', () => {
  const excel =
    '<html xmlns:x="urn:schemas-microsoft-com:office:excel"><body>' +
    '<table><tr>' +
    '<td align="center" bgcolor="#ffff00" style="mso-number-format:General">Total</td>' +
    '</tr></table></body></html>';

  it('is detected as Excel and Office HTML', () => {
    expect(isExcelHtml(excel)).to.be.true;
    expect(isOfficeHtml(excel)).to.be.true;
  });

  it('preserves cell background and alignment', () => {
    const cleaned = cleanOfficeHtml(excel);
    const doc = htmlToDoc(defaultSchema, cleaned);
    const out = docToHtml(doc);
    expect(out).to.include('Total');
    // Alignment survives on the wrapped paragraph.
    expect(out).to.match(/text-align:\s*center/);
    // Background survives on the cell.
    expect(out.toLowerCase()).to.include('background-color');
  });
});
